<script setup lang="ts">
import { ref } from 'vue';
import CopyButton from './CopyButton.vue';

const props = defineProps<{
  summary: string;
  totalHits: number;
}>();

const emit = defineEmits<{
  save: [value: string];
}>();

const editing = ref(false);
const draft = ref('');

function startEditing() {
  draft.value = props.summary;
  editing.value = true;
}

function cancelEditing() {
  editing.value = false;
}

function saveEditing() {
  emit('save', draft.value.trim());
  editing.value = false;
}
</script>

<template>
  <section
    class="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm"
    data-test="reviewer-summary-card"
  >
    <div class="flex items-center gap-1.5">
      <span class="text-[11px] font-semibold tracking-wide text-gray-400 uppercase">
        Reviewer summary
      </span>
      <span class="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">
        Optional
      </span>
      <CopyButton v-if="summary" class="ml-auto" :value="summary" label="reviewer summary" />
    </div>

    <template v-if="!editing">
      <p
        class="mt-1.5 text-[13px] break-words whitespace-pre-wrap"
        :class="summary ? 'text-gray-700' : 'text-gray-400'"
      >
        {{
          summary ||
          'Add your own conclusion in your words if the agent summary above misses something, or if QA will need your reasoning across the whole review.'
        }}
      </p>
      <button
        type="button"
        data-test="btn-edit-reviewer-summary"
        class="mt-2.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600"
        @click="startEditing"
      >
        {{ summary ? 'Edit summary' : 'Write summary' }}
      </button>
    </template>

    <template v-else>
      <textarea
        v-model="draft"
        data-test="reviewer-summary-textarea"
        :placeholder="`Your own conclusion across all ${totalHits} hits — what you decided, what evidence carried it, and anything the agent got wrong.`"
        class="mt-2.5 w-full resize-y rounded-md border border-gray-300 bg-gray-50 p-2.5 text-[13px] text-gray-900 outline-none"
        rows="4"
      />
      <div class="mt-2 flex gap-2">
        <button
          type="button"
          data-test="btn-cancel-reviewer-summary"
          class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600"
          @click="cancelEditing"
        >
          Cancel
        </button>
        <button
          type="button"
          data-test="btn-save-reviewer-summary"
          class="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
          @click="saveEditing"
        >
          Save summary
        </button>
      </div>
    </template>
  </section>
</template>
